/**
 * Scene themes for the DailyScene hero.
 *
 * Each theme is pure presentation config: the background gradient, whether a
 * sun or moon is shown (and where/what color), and whether the scene is dark
 * (so text and vocab cards flip to light-on-dark). Time-of-day themes are the
 * default; special-event themes (Samhain, Irish Week) override the time theme
 * when active.
 *
 * These are Tailwind utility strings on purpose. The scene is a swappable slot
 * (see `renderScene` in index.tsx), so a future pixel / hand-drawn / Rive scene
 * can ignore this config entirely and just read `theme.key`.
 */

export type TimeKey = "morning" | "day" | "evening" | "night"

export type Celestial = "sun" | "moon" | "none"

export type SceneImage = "lavender" | "moonlit" | "california" | "ireland"

export interface SceneTheme {
  key: string
  /** Irish + English label, e.g. "Maidin · Morning" */
  label: string
  /** Background photo used for the scene, chosen by time of day */
  image?: SceneImage
  /** Tailwind gradient classes for the sky (fallback behind the image) */
  gradient: string
  celestial: Celestial
  /** Gradient classes for the sun/moon disc */
  celestialColors: string
  /** Glow color behind the disc */
  celestialGlow: string
  /** Absolute-position classes for the disc */
  celestialPosition: string
  /** Show a starfield (night / event skies) */
  stars?: boolean
  /** Dark sky → light text + glassy cards */
  dark?: boolean
}

export const TIME_THEMES: Record<TimeKey, SceneTheme> = {
  morning: {
    key: "morning",
    label: "Maidin · Morning",
    image: "lavender",
    // gradient: "bg-gradient-to-b from-secondary-200 via-[#7e7ca0] to-earth-500",
    gradient: "bg-[#7e7ca0]",
    celestial: "sun",
    celestialColors: "from-amber-100 to-orange-300",
    celestialGlow: "bg-amber-200",
    celestialPosition: "bottom-16 left-10 md:left-24",
    dark: true,
  },
  day: {
    key: "day",
    label: "Lá · Day",
    image: "california",
    gradient: "bg-gradient-to-b from-[#e2eff1] via-[#4f9fc7] to-[#768a45]",
    celestial: "sun",
    celestialColors: "from-yellow-100 to-yellow-300",
    celestialGlow: "bg-yellow-200",
    celestialPosition: "top-8 right-12 md:right-32",
  },
  evening: {
    key: "evening",
    label: "Tráthnóna · Evening",
    image: "ireland",
    // gradient: "bg-gradient-to-b from-indigo-500 via-orange-300 to-rose-300",
    gradient: "bg-gradient-to-b from-[#3cb4eb] to-[#4f8739]",
    celestial: "sun",
    celestialColors: "from-orange-200 to-rose-400",
    celestialGlow: "bg-rose-400",
    celestialPosition: "bottom-12 right-12 md:right-28",
  },
  night: {
    key: "night",
    label: "Oíche · Night",
    image: "moonlit",
    // gradient: "bg-gradient-to-b from-slate-950 via-indigo-950 to-slate-800",
    gradient: "bg-black",
    celestial: "moon",
    celestialColors: "from-slate-50 to-slate-300",
    celestialGlow: "bg-slate-200",
    celestialPosition: "top-8 right-12 md:right-32",
    stars: true,
    dark: true,
  },
}

export const EVENT_THEMES: Record<string, SceneTheme> = {
  samhain: {
    key: "samhain",
    label: "Samhain · Halloween",
    image: "moonlit",
    // gradient: "bg-gradient-to-b from-slate-950 via-purple-950 to-orange-950",
    gradient: "bg-dark",
    celestial: "moon",
    celestialColors: "from-orange-100 to-orange-300",
    celestialGlow: "bg-orange-400",
    celestialPosition: "top-8 right-12 md:right-28",
    stars: true,
    dark: true,
  },
  "irish-week": {
    key: "irish-week",
    label: "Seachtain na Gaeilge · Irish Week",
    image: "lavender",
    // gradient: "bg-gradient-to-b from-green-500 via-green-300 to-emerald-100",
    gradient: "bg-dark",
    celestial: "sun",
    celestialColors: "from-yellow-100 to-yellow-300",
    celestialGlow: "bg-yellow-200",
    celestialPosition: "top-8 right-12 md:right-32",
  },
}

/** Event theme wins over time-of-day when an event is active. */
export const resolveTheme = (
  timeKey: TimeKey,
  event?: string | null
): SceneTheme => {
  if (event && EVENT_THEMES[event]) return EVENT_THEMES[event]
  return TIME_THEMES[timeKey] ?? TIME_THEMES.morning
}

/**
 * Theme for a section key: an event theme keyed by the section key (e.g. "samhain"),
 * else the time-of-day theme of the same name. Lets the sky follow the section.
 */
export const themeForSection = (sectionKey?: string): SceneTheme => {
  const key = sectionKey ?? "morning"
  return EVENT_THEMES[key] ?? TIME_THEMES[key as TimeKey] ?? TIME_THEMES.morning
}

/**
 * Client-side time-of-day bucket. Mirrors the greeting logic in
 * application-dynamic-datetime-label but with a four-way split so evening reads
 * distinctly from night. Exported for the connected component to reuse.
 */
export const currentTimeKey = (date: Date = new Date()): TimeKey => {
  const hour = date.getHours()
  if (hour >= 5 && hour < 12) return "morning"
  if (hour >= 12 && hour < 17) return "day"
  if (hour >= 17 && hour < 21) return "evening"
  return "night"
}

/** Active special event, if any (matches the greeting header's calendar). */
export const currentEvent = (date: Date = new Date()): string | null => {
  if (date.getMonth() === 9 && date.getDate() === 31) return "samhain"
  if (date.getMonth() === 2 && date.getDate() <= 18) return "irish-week"
  return null
}
